// ---------------------------------------------------------------------------
// corpus-few-shot.ts — turn a Channel-2 request into the `ontology.examples`
// payload cogo-cloudflare's `POST /api/v3/block/generate` renders as few-shot.
//
// WHAT THE CONSUMER DOES WITH THIS
// --------------------------------
// The route wraps `body.ontology.examples` into an `OntologyApi` whose
// `getExamples(archetype, limit)` is
//
//     (body.ontology.examples ?? []).filter(e => e.archetype === archetype)
//                                   .slice(0, limit)          // block-v3.ts
//
// and `hybrid-prompts.ts` calls `renderFewShot(stage, ontology, intent.archetype)`
// for ALL THREE stages (ui / events / flow) with `limit = 2`. `renderFewShot`
// picks the stage payload with
//
//     stage === "ui" ? ex.ui : stage === "events" ? ex.events : ex.flow
//
// and — this is the trap this module exists to close — SILENTLY prints whatever
// that is. An example without `events` renders
//
//     ### Example 1: <intent>
//     ```json
//
//     ```
//
// under "Canonical examples ... Use these as reference for correct shape and
// vocabulary": no error, no diagnostic, and the model is taught that the
// canonical events.cogo.json is empty. Sending nothing beats that, so every
// example crossing the wire passes `isCompleteBlockExample` first.
//
// THE ARCHETYPE JOIN
// ------------------
// `intent.archetype` is NOT ours to set: the route derives it with
// `blockSpecToIntent` → `inferArchetype(blockSpec.type)`, whose whole body is
// `ARCHETYPE_MAP[type.toLowerCase()] ?? type`. Nothing in the worker branches on
// archetype — it is a retrieval key and a prompt label only. So examples are
// tagged with `blockSpec.type` verbatim, which the worker echoes back unchanged
// for every value except the two `ARCHETYPE_MAP` entries that are not identity
// (see `WORKER_REMAPPED_BLOCK_TYPES`). Retrieval itself is done HERE, by
// `cogo:pattern` — the corpus field with real discriminating power (28 distinct
// over 251 rows, vs 5 for semanticType where `section` alone is 60%).
//
// Consequence, stated rather than hidden: the few-shot heading the model sees
// names the REQUEST's archetype (today always `section`), not the pattern the
// examples came from. The pattern is prefixed onto each example's `intent`
// line instead, which `renderFewShot` prints verbatim per example.
//
// WHY NOT tag examples with the pattern and put the pattern in `blockSpec.type`:
// `blockSpec.type` is load-bearing in the v2 fallback — `COMPLEX_BUNDLE_MAP`,
// `BLOCK_SPEC_V3_REGISTRY`, `blockSpecRequiresInteraction`'s regex and
// `buildBlockTypeSection` all read it. Moving a retrieval key into it would
// change generation behaviour for a retrieval reason.
// ---------------------------------------------------------------------------

import type { ChannelInput } from '../_shared/channel-types'
import { sanitizePatternToken } from '../_shared/sanitize-pattern'
import type { BlockSpec } from './intent-to-blockspec'

/**
 * `BlockExample` as the worker declares it (`hybrid-prompts.ts`). `intent` is
 * printed verbatim as the example heading; ui/events/flow are the stage
 * payloads `renderFewShot` selects between.
 */
export interface BlockExample {
  archetype: string
  intent: string
  ui: unknown
  events: unknown
  flow: unknown
}

/** One row of the generated bundle (see scripts/build-corpus-few-shot.mjs). */
export interface CorpusFewShotRow {
  id: string
  intent: string
  ui: unknown
  events: unknown
  flow: unknown
}

export interface CorpusFewShotBundle {
  schemaVersion: number
  /** `cogo:pattern` → ranked, capped rows. */
  patterns: Record<string, CorpusFewShotRow[]>
}

/**
 * The stage payload fields `renderFewShot` selects between, in the order it
 * names them. `__tests__/corpus-few-shot.test.ts` extracts these names out of
 * the real `renderFewShot` source and fails if this list stops matching.
 */
export const STAGE_PAYLOAD_FIELDS = ['ui', 'events', 'flow'] as const

/**
 * `renderFewShot` asks for at most 2 examples (`ontology.getExamples(archetype, 2)`).
 * Sending more only inflates a request body nobody reads past index 1.
 */
export const MAX_FEW_SHOT_EXAMPLES = 2

/**
 * The only `ARCHETYPE_MAP` keys (cogo-cloudflare `block-spec-adapter.ts`) whose
 * value is NOT the key itself: `table` and `list` both map to `list-view`. For
 * every other block type `inferArchetype` is the identity, which is what makes
 * tagging examples with `blockSpec.type` a correct join.
 *
 * A request whose type IS one of these gets NO examples rather than a guessed
 * remap — the same outcome as today, and a wrong archetype tag would silently
 * produce zero few-shot anyway.
 */
export const WORKER_REMAPPED_BLOCK_TYPES: ReadonlySet<string> = new Set(['table', 'list'])

/**
 * True when a value carries any content at all — i.e. it bottoms out in at
 * least one leaf, rather than in empty containers.
 *
 * The shallow form of this test (`Object.keys(payload).length > 0`) is not
 * enough, and the corpus proves it: `{"bindings": {}}` and `{"flows": {}}`
 * both have a key. Measured on the first cut of the bundle, 8 of 43 shipped
 * examples had zero event bindings and 5 had zero flows — including BOTH
 * `data-table` rows, so a "data table of orders" request would have been
 * taught that the canonical events.cogo.json for an interactive table is
 * empty. Same defect as the empty fenced block, one level down.
 *
 * Deliberately schema-agnostic: it asks "is there content", not "is the
 * `bindings` key populated", so it does not encode a guess about which
 * container key each stage happens to use today.
 */
function hasContent(value: unknown): boolean {
  if (value === undefined) return false
  if (value === null || typeof value !== 'object') return true
  const entries = Array.isArray(value) ? value : Object.values(value as object)
  return entries.length > 0 && entries.some(hasContent)
}

/**
 * The non-negotiable gate. A stage payload must be a plain object that
 * actually carries content; anything else renders as an empty (or
 * content-free) "canonical example".
 */

/**
 * 3-stage generation has no datasource file (D9: declaration ≠ execution).
 * Few-shot that teaches `loadDatasource` or `setState.params.target: "#{event.field}"`
 * was measured to survive 3 flow retries (form-submit 0/3) and to emit
 * `actionflow-restApi-params-complete` / `setState-target-undeclared`.
 * Sending nothing beats teaching those shapes — same rule as empty events/flow
 * payloads in `isCompleteBlockExample`.
 */
export function isThreeStageSafeFlowExample(candidate: unknown): boolean {
  if (!candidate || typeof candidate !== 'object') return false
  const flow = (candidate as Record<string, unknown>).flow
  if (flow == null) return false
  const blob = JSON.stringify(flow)
  if (/"action"\s*:\s*"loadDatasource"/.test(blob)) return false
  if (/"target"\s*:\s*"#\{event\.(field|name)\}"/.test(blob)) return false
  return true
}

export function isCompleteBlockExample(candidate: unknown): boolean {
  if (!candidate || typeof candidate !== 'object') return false
  const record = candidate as Record<string, unknown>
  for (const field of STAGE_PAYLOAD_FIELDS) {
    const payload = record[field]
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false
    if (!hasContent(payload)) return false
  }
  return true
}

/**
 * Resolve the request's `cogo:pattern`, or null when it has none.
 *
 * A pattern matches when EVERY one of its kebab tokens appears in the intent's
 * token set; the most specific match wins, alphabetical on a tie so the choice
 * is deterministic. Nothing is invented: the vocabulary argument is the
 * bundle's own key set, which is the index's `cogo:pattern` values.
 *
 * No match → null → no examples. Forcing a near-match would put an unrelated
 * block under a heading that tells the model it is canonical.
 *
 * `sanitizePatternToken` is the same normaliser Channel 1 puts in front of the
 * repository, so an intent that Channel 1 could not anchor cannot anchor here
 * either. Its documented behaviour also means a pure non-ASCII intent (e.g. an
 * all-Korean prompt) collapses to the empty token set and gets no examples.
 */
export function resolvePatternForIntent(
  intentText: string | undefined,
  vocabulary: readonly string[],
): string | null {
  const tokens = new Set(
    sanitizePatternToken(intentText ?? '')
      .split('-')
      .filter((token) => token.length > 0),
  )
  if (tokens.size === 0) return null

  let best: string | null = null
  let bestTokenCount = 0
  for (const pattern of [...vocabulary].sort()) {
    const parts = pattern.split('-').filter((part) => part.length > 0)
    if (parts.length === 0) continue
    if (!parts.every((part) => tokens.has(part))) continue
    if (parts.length > bestTokenCount) {
      best = pattern
      bestTokenCount = parts.length
    }
  }
  return best
}

let bundlePromise: Promise<CorpusFewShotBundle> | null = null

/**
 * Lazily load the generated bundle.
 *
 * The import is DYNAMIC on purpose: `dispatch.ts` is reachable from the router
 * entry, so a static import would fold ~300KB of corpus JSON into the index
 * chunk that `scripts/studio-bundle-budget.mjs` caps. As a dynamic import it is
 * a separate chunk fetched only when a real-mode generation actually runs.
 */
export function loadCorpusFewShotBundle(): Promise<CorpusFewShotBundle> {
  bundlePromise ??= import('./corpus-few-shot-v1.json').then(
    (module) => (module.default ?? module) as unknown as CorpusFewShotBundle,
  )
  return bundlePromise
}

/** Test seam — drop the memoised bundle. */
export function resetCorpusFewShotBundleCache(): void {
  bundlePromise = null
}

/**
 * Examples for one Channel-2 request, or `[]` when the corpus has nothing
 * honest to offer.
 */
export async function corpusFewShotExamples(
  input: ChannelInput,
  blockSpec: BlockSpec,
  load: () => Promise<CorpusFewShotBundle> = loadCorpusFewShotBundle,
): Promise<BlockExample[]> {
  if (input.kind !== 'intent') return []
  const archetype = blockSpec.type
  if (!archetype || WORKER_REMAPPED_BLOCK_TYPES.has(archetype.toLowerCase())) return []

  let bundle: CorpusFewShotBundle
  try {
    bundle = await load()
  } catch {
    // A missing/broken bundle must degrade to today's behaviour (no examples,
    // v2 pipeline), never to a half-populated prompt.
    return []
  }
  const patterns = bundle?.patterns
  if (!patterns || typeof patterns !== 'object') return []

  const pattern = resolvePatternForIntent(input.pattern ?? input.semanticType, Object.keys(patterns))
  if (!pattern) return []

  const rows = patterns[pattern]
  if (!Array.isArray(rows)) return []

  return rows
    .filter((row) => isCompleteBlockExample(row))
    .filter((row) => isThreeStageSafeFlowExample(row))
    .slice(0, MAX_FEW_SHOT_EXAMPLES)
    .map((row) => ({
      archetype,
      intent: row.intent,
      ui: row.ui,
      events: row.events,
      flow: row.flow,
    }))
}
